import React from "react";
import {FiChevronDown, FiChevronUp, FiFilter, FiPlus, FiX} from "react-icons/fi";
import {ColumnFilter, getFilterableColumn} from "./filter-types";
import {Column} from "@tanstack/react-table";
import {TableItemForGridFormat} from "./data";
import {FilterSectionType} from "./filter-panel";
import {TagOptionWithMeta} from "./tag-filter-dropdown";

/**
 * Props for the ColumnFilterDisplay component
 */
interface ColumnFilterDisplayProps {
    columnId: string;
    filters: ColumnFilter[];
    onAddFilter: (columnId: string) => void;
    onRemoveFilter: (filterId: string) => void;
}

/**
 * Displays applied filters for a specific column in the header.
 * Shows filter values as pills with remove buttons, plus an add button.
 */
export const ColumnFilterDisplay: React.FC<ColumnFilterDisplayProps> = ({
    columnId,
    filters,
    onAddFilter,
    onRemoveFilter,
}) => {
    const columnFilters = filters.filter(f => f.columnId === columnId && f.value.trim());
    const column = getFilterableColumn(columnId);

    if (!column) {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center gap-1 mt-1">
            {columnFilters.map(filter => (
                <span
                    key={filter.id}
                    className="inline-flex items-center gap-1 rounded-full bg-[#ececf3] px-2 py-0.5 text-xs text-[#6d6d8a]"
                    title={`${filter.searchType}: ${filter.value}`}
                >
                    <span className="max-w-[80px] truncate">{filter.value}</span>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveFilter(filter.id);
                        }}
                        className="hover:text-red-600 transition-colors"
                    >
                        <FiX className="w-[0.75em] h-[0.75em]" />
                    </button>
                </span>
            ))}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onAddFilter(columnId);
                }}
                className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-[#a1a1b6] px-1.5 py-0.5 text-zinc-500 hover:border-[#8c8ca3] hover:text-[#6d6d8a] hover:bg-[#ececf3] transition-colors"
                title={`Add filter for ${column.label}`}
            >
                <FiFilter className="w-[0.625em] h-[0.625em]" />
                <FiPlus className="w-[0.625em] h-[0.625em]" />
            </button>
        </div>
    );
};

/**
 * Header cell that shows column label, sort indicators, and applied filters
 */
interface FilterableHeaderProps {
    columnId: string;
    label: string;
    filters: ColumnFilter[];
    onAddFilter: (columnId: string) => void;
    onRemoveFilter: (filterId: string) => void;
    column?: Column<TableItemForGridFormat, unknown>;
}

export const FilterableHeader: React.FC<FilterableHeaderProps> = ({
    columnId,
    label,
    filters,
    onAddFilter,
    onRemoveFilter,
    column,
}) => {
    const filterableColumn = getFilterableColumn(columnId);
    const isFilterable = !!filterableColumn;
    const canSort = column?.getCanSort() ?? false;
    const sortDirection = column?.getIsSorted();
    const sortIndex = column?.getSortIndex();

    const handleSortClick = (e: React.MouseEvent) => {
        if (!canSort || !column) return;
        // Shift+click for multi-sort, regular click for single sort
        column.toggleSorting(undefined, e.shiftKey);
    };

    return (
        <div className="flex flex-col gap-1">
            <div
                className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-zinc-600 ${
                    canSort ? 'cursor-pointer hover:text-zinc-900 select-none' : ''
                }`}
                onClick={handleSortClick}
                title={canSort ? (sortDirection ? 'Click to change sort, Shift+click for multi-sort' : 'Click to sort, Shift+click for multi-sort') : undefined}
            >
                <span>{label}</span>
                {canSort && (
                    <span className="flex items-center">
                        {sortDirection === 'asc' && <FiChevronUp className="w-3 h-3 text-blue-600" />}
                        {sortDirection === 'desc' && <FiChevronDown className="w-3 h-3 text-blue-600" />}
                        {!sortDirection && <FiChevronDown className="w-3 h-3 text-gray-300" />}
                        {sortIndex !== undefined && sortIndex >= 0 && (
                            <span className="ml-0.5 text-[0.65rem] text-blue-500">{sortIndex + 1}</span>
                        )}
                    </span>
                )}
            </div>
            {isFilterable && (
                <ColumnFilterDisplay
                    columnId={columnId}
                    filters={filters}
                    onAddFilter={onAddFilter}
                    onRemoveFilter={onRemoveFilter}
                />
            )}
        </div>
    );
};

/**
 * Props for the TagColumnHeader component
 */
interface TagColumnHeaderProps {
    /** Column label to display */
    label: string;
    /** Filter section type for quick-filter */
    filterType: FilterSectionType;
    /** Callback when filter button is clicked */
    onFilterClick: (filterType: FilterSectionType) => void;
    /** Number of active filters for this type */
    activeFilterCount: number;
    /** Optional column for sorting functionality */
    column?: Column<TableItemForGridFormat, unknown>;
    /** Active tag filters to display inline */
    activeFilters?: TagOptionWithMeta[];
    /** Callback to remove a tag filter */
    onRemoveFilter?: (tagId: string) => void;
}

/**
 * Header cell for tag-based columns (Codes, Document Requests, Entities).
 * Shows column label with a filter icon button that expands the filter panel
 * and scrolls to the relevant section. Also displays active filters inline.
 */
export const TagColumnHeader: React.FC<TagColumnHeaderProps> = ({
    label,
    filterType,
    onFilterClick,
    activeFilterCount,
    column,
    activeFilters,
    onRemoveFilter,
}) => {
    const canSort = column?.getCanSort() ?? false;
    const sortDirection = column?.getIsSorted();
    const sortIndex = column?.getSortIndex();

    const handleSortClick = (e: React.MouseEvent) => {
        if (!canSort || !column) return;
        column.toggleSorting(undefined, e.shiftKey);
    };

    const handleFilterClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onFilterClick(filterType);
    };

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
                <div
                    className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-zinc-600 ${
                        canSort ? 'cursor-pointer hover:text-zinc-900 select-none' : ''
                    }`}
                    onClick={handleSortClick}
                    title={canSort ? (sortDirection ? 'Click to change sort, Shift+click for multi-sort' : 'Click to sort, Shift+click for multi-sort') : undefined}
                >
                    <span>{label}</span>
                    {canSort && (
                        <span className="flex items-center">
                            {sortDirection === 'asc' && <FiChevronUp className="w-3 h-3 text-blue-600" />}
                            {sortDirection === 'desc' && <FiChevronDown className="w-3 h-3 text-blue-600" />}
                            {!sortDirection && <FiChevronDown className="w-3 h-3 text-gray-300" />}
                            {sortIndex !== undefined && sortIndex >= 0 && (
                                <span className="ml-0.5 text-[0.65rem] text-blue-500">{sortIndex + 1}</span>
                            )}
                        </span>
                    )}
                </div>
                <button
                    onClick={handleFilterClick}
                    className={`p-1 rounded transition-colors ${
                        activeFilterCount > 0
                            ? 'bg-[#c4b5fd] text-[#1c1c24] hover:bg-[#b3a3ec]'
                            : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100'
                    }`}
                    title={`Filter by ${label.toLowerCase()}`}
                >
                    <FiFilter className="w-3 h-3" />
                </button>
            </div>
            {/* Inline active filters */}
            {activeFilters && activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 mt-1">
                    {activeFilters.slice(0, 3).map(filter => (
                        <span
                            key={filter.id}
                            className="inline-flex items-center gap-1 rounded-full bg-[#c4b5fd] px-2 py-0.5 text-xs text-[#1c1c24]"
                            title={filter.name}
                        >
                            <span className="max-w-[60px] truncate">{filter.name}</span>
                            {onRemoveFilter && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveFilter(filter.id);
                                    }}
                                    className="hover:text-red-600 transition-colors"
                                >
                                    <FiX className="w-[0.75em] h-[0.75em]" />
                                </button>
                            )}
                        </span>
                    ))}
                    {activeFilters.length > 3 && (
                        <span
                            className="inline-flex items-center rounded-full bg-[#ececf3] px-1.5 py-0.5 text-[10px] text-[#6d6d8a] cursor-pointer hover:bg-[#d5d5e0]"
                            onClick={handleFilterClick}
                            title="Click to see all filters"
                        >
                            +{activeFilters.length - 3}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
